
import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForm } from "@/context/FormContext";
import { FormFieldType } from "@/types/form";
import FieldWrapper from "./FieldWrapper";
import { useTranslation } from "react-i18next";
import { tField } from "../utils/i18nField";

interface CheckboxFieldProps {
  element: FormFieldType;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({ element }) => {
  const { formData, setField } = useForm();
  const { t } = useTranslation();
  
  // Handle single checkbox for boolean values (like terms acceptance)
  const handleSingleCheckboxChange = (checked: boolean) => {
    setField(element.name!, checked);
  };

  // Handle multiple checkboxes (array of values)
  const handleMultipleCheckboxChange = (checked: boolean, value: string, textInputName?: string) => {
    const currentValues = (formData[element.name!] as string[]) || [];
    let newValues: string[];
    
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(val => val !== value);
    }
    
    setField(element.name!, newValues);
    
    // If unchecking "Other" option, clear the text input
    if (!checked && value === "Other" && textInputName) {
      handleTextOtherChange("", textInputName);
    }
  };

  const handleTextOtherChange = (value: string, textInputName: string) => {
    if (textInputName) {
      setField(textInputName, value);
    }
  };

  if (!element.options) return null;

  // If it's a single checkbox for boolean (like terms acceptance)
  if (element.options.length === 1 && typeof element.options[0].value === 'boolean') {
    const option = element.options[0];
    const isChecked = !!formData[element.name!];

    return (
      <FieldWrapper element={element}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${element.id}`}
            checked={isChecked}
            onCheckedChange={(checked) => handleSingleCheckboxChange(!!checked)}
          />
          <Label
            htmlFor={`${element.id}`}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {tField(t, option.label)}
          </Label>
        </div>
      </FieldWrapper>
    );
  }

  // For multiple checkboxes
  return (
    <FieldWrapper element={element}>
      <div className="space-y-3">
        {element.options.map((option) => {
          const currentValues = (formData[element.name!] as string[]) || [];
          const isChecked = currentValues.includes(option.value.toString());
          const textInputName = option.hasTextInput ? option.textInputName : undefined;
          
          return (
            <div key={option.value.toString()} className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${element.id}-${option.value}`}
                  checked={isChecked}
                  onCheckedChange={(checked) => 
                    handleMultipleCheckboxChange(checked as boolean, option.value.toString(), textInputName)
                  }
                />
                <Label
                  htmlFor={`${element.id}-${option.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {tField(t, option.label)}
                </Label>
              </div>
              
              {option.hasTextInput && isChecked && textInputName && (
                <Input
                  value={(formData[textInputName] as string) || ""}
                  onChange={(e) => handleTextOtherChange(e.target.value, textInputName)}
                  placeholder="Please specify"
                  className="ml-6 w-full max-w-sm"
                />
              )}
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
};

export default CheckboxField;
